import { Shield, Users, CheckCircle, Building2, Phone, Mail, ArrowLeft, Wrench, UserCheck, Briefcase, Tag } from 'lucide-react'
import SEOHead from '../../components/SEOHead'
import { Link } from 'react-router-dom'

export default function CPT() {
  const services = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "Professional Drivers",
      description: "Fully vetted, professional drivers ready to keep your fleet moving safely and efficiently."
    },
    {
      icon: <Wrench className="h-8 w-8" />,
      title: "Skilled Engineering Personnel", 
      description: "Qualified engineers to ensure your vehicles remain safe, compliant, and road ready."
    },
    {
      icon: <Briefcase className="h-8 w-8" />,
      title: "Back-Office Support",
      description: "From operations managers to compliance officers, we provide the expertise you need behind the scenes."
    }
  ]

  return (
    <div className="min-h-screen">
      <SEOHead
        title="CPT Partnership - Exclusive Staffing Services | C9 Recruitment"
        description="Exclusive 10% discount for CPT members on C9 Recruitment's professional staffing services. Drivers, engineers, and back-office support for the bus and coach industry."
        keywords="CPT partnership, Confederation of Passenger Transport, bus drivers, coach drivers, transport staffing, CPT member discount"
        canonical="/partners/cpt"
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-24 md:py-32">
          <div className="max-w-4xl mx-auto">
            {/* Back to Partners link */}
            <Link 
              to="/partners" 
              className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Partners
            </Link>
            
            <div className="text-center">
              <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-6 backdrop-blur-sm">
                CPT Partnership
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Exclusive Staffing Services
                <span className="block text-3xl md:text-4xl mt-4 font-normal">
                  for{" "}
                  <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                    CPT Members
                  </span>
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
                Trusted partner to the bus and coach industry, offering specialized staffing 
                solutions with exclusive member benefits
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Overview */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-c9-blue mb-6">
                Comprehensive{" "}
                <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                  Staffing Solutions
                </span>
              </h2>
              
              <p className="text-gray-700 leading-relaxed text-lg">
                C9 Recruitment, a trusted partner to the bus and coach industry, offers a wide 
                range of staffing solutions tailored to the needs of operators across the UK.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                From sourcing high-quality, reliable drivers to skilled engineers and essential 
                back-office personnel, C9 is here to support your operations with professional, 
                vetted candidates who understand the demands of the transport industry.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                Our partnership with the Confederation of Passenger Transport ensures that CPT 
                members receive priority service and exclusive benefits, helping you maintain 
                the highest standards in passenger transport.
              </p>
            </div>

            <div className="space-y-6">
              {/* Member Offer Box */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                <h3 className="text-2xl font-bold text-c9-blue mb-6 flex items-center">
                  <Tag className="h-7 w-7 text-green-600 mr-3" />
                  Exclusive CPT Member Offer
                </h3>
                
                <div className="bg-green-600 text-white rounded-xl p-6 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">10% OFF</div>
                    <div className="text-lg">Staffing Services*</div>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Discount Code:</strong> 'CPT10'
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Valid for:</strong> 90 days from booking
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong className="text-c9-blue">Simply provide</strong> the discount code when booking
                    </span>
                  </li>
                </ul>
                
                <p className="text-sm text-gray-600 mt-4 italic">
                  *Discount applies to fees, not candidate salary
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section - Centered */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-r from-c9-orange to-orange-400 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Get in Touch Today</h3>
              <p className="text-center text-white/90 mb-6">
                To find out how C9 Recruitment can support your business, or to take advantage 
                of this exclusive CPT member discount:
              </p>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="tel:02081524574" 
                    className="flex items-center justify-center space-x-3 text-white hover:text-gray-200 transition-colors group bg-white/20 rounded-lg p-4 flex-1"
                  >
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <Phone className="h-5 w-5" />
                    </div>
                    <span className="text-lg">020 8152 4574</span>
                  </a>
                  <a 
                    href="tel:07733073120" 
                    className="flex items-center justify-center space-x-3 text-white hover:text-gray-200 transition-colors group bg-white/20 rounded-lg p-4 flex-1"
                  >
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <Phone className="h-5 w-5" />
                    </div>
                    <span className="text-lg">07733 073 120</span>
                  </a>
                </div>
                <a 
                  href="mailto:commercial@C9-recruitment.com" 
                  className="flex items-center justify-center space-x-3 text-white hover:text-gray-200 transition-colors group bg-white/20 rounded-lg p-4"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <span className="text-lg">commercial@C9-recruitment.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Our Specialist{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Services
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional staffing solutions designed specifically for the bus and coach industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center text-white mb-6 mx-auto">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-c9-blue mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed text-center">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Why Choose{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                C9 Recruitment
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <UserCheck className="h-8 w-8" />,
                title: "Industry Expertise",
                description: "Deep understanding of the bus and coach sector with years of specialized recruitment experience."
              },
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Fully Vetted Candidates",
                description: "Comprehensive screening and vetting processes ensure you get reliable, qualified professionals."
              },
              {
                icon: <Building2 className="h-8 w-8" />,
                title: "Trusted Partner",
                description: "Established relationships with operators across the UK, delivering consistent, quality service."
              }
            ].map((benefit, index) => (
              <div key={index} className="group text-center">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-c9-blue to-c9-blue-alt rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-c9-blue mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-c9-orange to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
        
        <div className="relative container-full text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Access Your CPT Member Benefits?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Don't forget to mention discount code 'CPT10' when you get in touch to claim 
            your exclusive 10% discount on staffing services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:02081524574" 
              className="bg-white text-c9-orange px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center justify-center"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call 020 8152 4574
            </a>
            <a 
              href="mailto:commercial@C9-recruitment.com" 
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-c9-orange transition-all duration-300 inline-flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
